import { SignUp, useAuth } from "@clerk/clerk-react";
import { useClerkComponentTracking } from "@/hooks/useClerkComponentTracking";
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { captureEvent } from "@/utils/posthog";
import { useRootStore } from "@/state/RootStore";
import ExamTypeSelection from "../ExamTypeSelection";
import { APP_TO_BUILD, PracticeTestType } from "../../../appsConfig";
import { observer } from "mobx-react";
import { useIsMobileWeb } from "@/hooks/useIsMobileWeb";
import DownloadAppCta, {
  DOWNLOAD_APP_CTA_CLEARANCE_CLASS,
} from "@/components/mobile/DownloadAppCta";
import { AuthLayout, clerkAuthAppearance } from "./AuthLayout";
import { clearPendingSignUpExamSelection } from "@/utils/pendingSignUpExamSelection";
import SplashScreen from "@/components/SplashScreen";

const SignUpPage = observer(() => {
  const currentUrl = window.location.origin;
  const redirectLinkSignUp = `${currentUrl}/?afterSignUp=true`;
  const isMobileWeb = useIsMobileWeb();
  const { isLoaded, isSignedIn } = useAuth();
  const initializedRef = useRef(false);
  const [selectionInitialized, setSelectionInitialized] = useState(false);

  const store = useRootStore();
  useClerkComponentTracking("sign_up");

  useEffect(() => {
    if (!isLoaded || isSignedIn || initializedRef.current) return;
    initializedRef.current = true;
    captureEvent("sign_up_view");
    clearPendingSignUpExamSelection();
    store.mainAppStore.resetSelectedStateCode();
    store.questionsStore.loadQuestions();
    setSelectionInitialized(true);
  }, [isLoaded, isSignedIn, store]);

  if (!isLoaded || (!isSignedIn && !selectionInitialized)) {
    return <SplashScreen />;
  }

  if (isSignedIn) {
    return <Navigate replace to="/" />;
  }

  if (!store.mainAppStore.selectedStateCode && PracticeTestType.byState) {
    return (
      <div
        className={
          isMobileWeb
            ? `w-full min-h-screen ${DOWNLOAD_APP_CTA_CLEARANCE_CLASS}`
            : "w-full h-full"
        }
      >
        <ExamTypeSelection standalone inSignUp />
        {isMobileWeb && <DownloadAppCta />}
      </div>
    );
  }

  return (
    <AuthLayout isMobileWeb={isMobileWeb}>
      <SignUp
        appearance={clerkAuthAppearance}
        routing="virtual"
        forceRedirectUrl={redirectLinkSignUp}
        fallbackRedirectUrl={redirectLinkSignUp}
        signInUrl="/sign-in?fromSignUp=true"
        // Redirect rather than popup: in-app browsers (Instagram, Facebook,
        // LinkedIn) block popups silently, so social traffic tapping a social
        // sign-in button saw nothing happen and left — producing no error and
        // no support ticket, which made the loss invisible in analytics.
        oauthFlow="redirect"
        unsafeMetadata={{ appType: APP_TO_BUILD }}
      />
      {isMobileWeb && <DownloadAppCta />}
    </AuthLayout>
  );
});

export default SignUpPage;
